import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { DateTime } from 'scaleflow/utils/date-time';
import { TOTAL_WORKING_DAYS } from 'scaleflow/models/investment-scenario';

export default class StrategyCalculation extends Component {
  @service organisation;

  divisions = 5;

  get scenario() {
    throw new Error('Override getter to retrieve scenario');
  }

  get start_date() {
    return this.args?.start_date ?? this.scenario?.start_date;
  }

  get end_date() {
    return this.args?.end_date ?? this.scenario?.end_date;
  }

  get scenarioPWLimits() {
    return {
      'operations and maintenance': {
        lower_limit: this.scenario.get('limit_lower_operations'),
        upper_limit: this.scenario.get('limit_upper_operations'),
      },
      vision: {
        lower_limit: this.scenario.get('limit_lower_vision'),
        upper_limit: this.scenario.get('limit_upper_vision'),
      },
      'customer request current': {
        lower_limit: this.scenario.get('limit_lower_customer'),
        upper_limit: this.scenario.get('limit_upper_customer'),
      },
    };
  }

  @action
  distribution(main_field, scenario) {
    if (main_field == undefined) return 1;

    const vision = +scenario.get('distribution_vision');
    const operations = +scenario.get('distribution_operations');
    const customer = +scenario.get('distribution_customer');

    const sum = vision + operations + customer;
    return +scenario[`distribution_${main_field}`] / sum;
  }

  @action
  calculateWeeks(start_date, end_date) {
    if (!start_date && !end_date) return 13;
    return DateTime.from(end_date).diff(
      DateTime.from(start_date),
      'weeks',
      true,
    );
  }
  /**
   *
   * @param {Object} scenario - contains form values
   * @param {string} source - in percentage i.e. a value of 20 means 20%

   */
  @action
  calculatePwCapacity(scenario, source, useStaticNumberOfWeeks = true) {
    try {
      let pwCapacity = 0;
      const distribution = this.distribution(source, scenario);
      if (useStaticNumberOfWeeks) {
        pwCapacity = this.pw_initiatives(scenario) * distribution;
      } else {
        const ftes = +scenario.fte;
        const initiative_capacity = this.percentage_initiatives(scenario) / 100;
        const start_date = this.start_date;
        const end_date = this.end_date;
        const weeks = useStaticNumberOfWeeks
          ? 13
          : this.calculateWeeks(start_date, end_date);
        pwCapacity = ftes * initiative_capacity * distribution * weeks;
      }
      if (isNaN(pwCapacity)) return null;
      else return pwCapacity;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  @action
  calculateCostFromPersonWeeks(weeks) {
    const costs = this.organisation.ppw * weeks;
    if (isNaN(costs)) return '-';
    return costs;
  }

  @action
  calculateCost(scenario, source) {
    const pwCapacity = this.calculatePwCapacity(scenario, source);
    const cost = pwCapacity * this.organisation.ppw;
    if (isNaN(cost)) return '-';
    return cost;
  }

  @action
  calculateInitiativesLimit(scenario, source, limit_type) {
    const limit = scenario[`limit_${limit_type}_${source}`];
    const capacity = this.calculatePwCapacity(scenario, source);
    const result = Math.ceil(capacity / limit);
    if (isNaN(result)) return '-';
    return result;
  }

  @action
  calculateInvestmentSizes(scenario, source, limit_type) {
    const limit = scenario[`limit_${limit_type}_${source}`];
    const initiative_capacity = this.percentage_initiatives(scenario) / 100;
    const capacity_ppw = this.organisation.ppw * initiative_capacity;
    const result = (limit * capacity_ppw).toFixed(2);
    if (isNaN(result)) return '-';
    return result;
  }

  calculateInterval(upper_limit, lower_limit) {
    return (Number(upper_limit) - Number(lower_limit)) / this.divisions;
  }

  @action
  calculateAdvisedRange(lowerLimit, upperLimit, ceil = false) {
    const interval = this.calculateInterval(upperLimit, lowerLimit);
    let lower_range = Number(lowerLimit) + interval;
    let upper_range = Number(upperLimit) - interval;
    if (ceil) {
      lower_range = Math.ceil(lower_range);
      upper_range = Math.ceil(upper_range);
    }
    if (isNaN(lower_range)) lower_range = '-';
    if (isNaN(upper_range)) upper_range = '-';

    return [lower_range, upper_range];
  }

  pw_flow(scenario) {
    return (
      scenario.fte * (this.percentage_flow(scenario) / 100) * scenario.weeks
    );
  }

  pw_unavailable(scenario) {
    return Math.round(
      scenario.fte *
        (this.percentage_unavailable(scenario) / 100) *
        scenario.weeks,
    );
  }

  pw_initiatives(scenario) {
    return (
      this.pw_total(scenario) -
      (this.pw_flow(scenario) + this.pw_unavailable(scenario))
    );
  }

  pw_vision(scenario) {
    return this.pw_number(scenario, scenario.distribution_vision);
  }

  pw_customer(scenario) {
    return this.pw_number(scenario, scenario.distribution_customer);
  }

  pw_operations(scenario) {
    return this.pw_number(scenario, scenario.distribution_operations);
  }

  pw_total(scenario) {
    return this.pw_number(scenario);
  }

  pw_number(scenario, source) {
    if (source == undefined) {
      return scenario.fte * scenario.weeks;
    } else {
      return (
        (source / this.total_distribution(scenario)) *
        scenario.fte *
        (this.percentage_initiatives(scenario) / 100) *
        scenario.weeks
      );
    }
  }

  total_distribution(scenario) {
    return (
      scenario.distribution_vision +
      scenario.distribution_customer +
      scenario.distribution_operations
    );
  }

  percentage_initiatives(scenario) {
    if (!this.unavailable_portion(scenario))
      return +scenario.initiative_capacity;
    return Math.round(
      ((100 - +this.unavailable_portion(scenario)) *
        +scenario.initiative_capacity) /
        100,
    );
  }

  percentage_flow(scenario) {
    if (!this.unavailable_portion(scenario))
      return 100 - +scenario.initiative_capacity;
    return Math.round(
      ((100 - +this.unavailable_portion(scenario)) *
        (100 - +scenario.initiative_capacity)) /
        100,
    );
  }

  percentage_unavailable(scenario) {
    return +this.unavailable_portion(scenario);
  }

  unavailable_portion(scenario) {
    let percent_unavailable = 0;
    if (!scenario.unavailable_time);
    else {
      const total_unavailable = +scenario.unavailable_time.total;
      percent_unavailable = Math.floor(
        (total_unavailable / TOTAL_WORKING_DAYS) * 100,
      );
    }
    return percent_unavailable;
  }
}
